import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { LANGUAGES_ARRAY } from "../../constants/languages";
import Wrapper from "../wrapper";
import React, { useEffect, useRef } from "react";
import GlobalWidgets from "../widgets/GlobalWidgets";
import { UpgradePlanModal } from "../modals/upgradePlanModal/upgradePlanModal";
import { gql, useQuery, useReactiveVar } from "@apollo/client";
import {
  isActiveTrialShow,
  isTrialEndedShow,
  isPremiumOnlyRestricted,
  isPaymentFailedShow,
  isHaveBeenBlocked,
  isPaymentError,
  isPublishShow,
  isShowNewProjectModal,
  isShowTemplateModal,
  isUpgradeShow,
  moveItem,
  renameFolder,
} from "../../graphql/state";
import { PublishModal } from "../modals/publishModal/publishModal";
import TemplateModal from "../modals/templateModal";
import { MoveModal } from "../modals/moveModal/MoveModal";
import { CustomToast } from "../inputs/toast";
import { RenameFolderModal } from "../modals/renameFolderModal/RenameFolderModal";
import { CreateProjectModal } from "../modals/createProjectModal";
import { useUserQuery } from "../../graphql/menu.hooks";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { ProjectLimitModal, PublishLimitModal } from "../modals/limit-modals";
import ActiveTrialModal from "../modals/activeTrialModal/ActiveTrialModal";
import TrialEndedModal from "../modals/trialEndedModal";
import {
  getTrialModal,
  isPaymentFailed,
  isPremiumOnlyRestrictedAccess,
  isRestrictedAllowedRoute,
  SubscriptionAccess,
} from "../../services/subscriptionTrial";
import { Button, Spinner } from "react-bootstrap";

const PAGES_WITHOUT_MENU = ["/payment/successful"];

const SUBSCRIPTION_ACCESS_QUERY = gql`
  query SubscriptionAccess {
    subscriptionAccess {
      ownerId
      paidOnly
      canWrite
      accessState
      billingState
      trialActive
      trialEndsAt
    }
  }
`;

type SubscriptionAccessQuery = {
  subscriptionAccess: SubscriptionAccess;
};

const Layout = () => {
  const { locale = "en" } = useParams();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: userData, loading } = useUserQuery();
  const {
    data: subscriptionAccessData,
    loading: subscriptionAccessLoading,
    error: subscriptionAccessError,
    refetch: refetchSubscriptionAccess,
  } = useQuery<SubscriptionAccessQuery>(SUBSCRIPTION_ACCESS_QUERY, {
    skip: loading || !userData?.user,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const autoShownTrialModal = useRef<"active" | "ended" | null>(null);
  const lng = useGetUrlLocale();

  const isShowUpgrade = useReactiveVar(isUpgradeShow);
  const isShowActiveTrial = useReactiveVar(isActiveTrialShow);
  const isShowTrialEnded = useReactiveVar(isTrialEndedShow);
  const isRestricted = useReactiveVar(isPremiumOnlyRestricted);
  const publishModal = useReactiveVar(isPublishShow);
  const templateModal = useReactiveVar(isShowTemplateModal);
  const moveItemState = useReactiveVar(moveItem);
  const renameFolderState = useReactiveVar(renameFolder);
  const isPaymentErr = useReactiveVar(isPaymentError);
  const isShowProjectModal = useReactiveVar(isShowNewProjectModal);
  const isBlocked = useReactiveVar(isHaveBeenBlocked);

  useEffect(() => {
    if (LANGUAGES_ARRAY.includes(locale)) {
      i18n.changeLanguage(locale);
    } else {
      navigate("/", { replace: true });
    }
  }, [locale, i18n, navigate]);

  useEffect(() => {
    if (!userData?.user && !loading) {
      window.location.href = `${process.env.REACT_APP_ME_PAGE_URL}${lng}/login`;
    }
  }, [userData, loading, lng]);

  const subscriptionAccess = subscriptionAccessData?.subscriptionAccess;
  const premiumOnlyRestricted =
    isPremiumOnlyRestrictedAccess(subscriptionAccess);
  const shouldBlockRoute =
    premiumOnlyRestricted && !isRestrictedAllowedRoute(pathname);
  const isAccessPending =
    loading ||
    subscriptionAccessLoading ||
    (!!userData?.user && !subscriptionAccessData && !subscriptionAccessError);
  const hasSubscriptionAccessError =
    !!userData?.user && !!subscriptionAccessError;

  useEffect(() => {
    if (!subscriptionAccess) return;

    isPremiumOnlyRestricted(premiumOnlyRestricted);
    isPaymentFailedShow(isPaymentFailed(subscriptionAccess));

    const modal = getTrialModal(subscriptionAccess);
    if (!modal || autoShownTrialModal.current === modal) return;

    autoShownTrialModal.current = modal;
    if (modal === "active") isActiveTrialShow(true);
    if (modal === "ended") isTrialEndedShow(true);
  }, [subscriptionAccess, premiumOnlyRestricted]);

  useEffect(() => {
    if (!shouldBlockRoute) return;

    isTrialEndedShow(true);
    navigate(`${lng}/projects`, { replace: true });
  }, [shouldBlockRoute, navigate, lng]);

  useEffect(() => {
    if (isRestricted && subscriptionAccessData) refetchSubscriptionAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isRestricted]);

  const isPageWithoutMenu = PAGES_WITHOUT_MENU.some((page) =>
    pathname.includes(page)
  );

  if (isPageWithoutMenu) {
    return <Outlet />;
  }

  return (
    <>
      <Wrapper>
        <GlobalWidgets />
        {isAccessPending ? (
          <Spinner animation="border" role="status" />
        ) : hasSubscriptionAccessError ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-3 py-5">
            <p className="mb-0 text-center text-gray">
              {t(
                "payment.Something went wrong. Try again later or contact support."
              )}
            </p>
            <Button onClick={() => refetchSubscriptionAccess()}>
              {t("Try again")}
            </Button>
          </div>
        ) : (
          !shouldBlockRoute && <Outlet />
        )}
      </Wrapper>
      {isShowActiveTrial && (
        <ActiveTrialModal
          show={isShowActiveTrial}
          trialEndsAt={subscriptionAccess?.trialEndsAt}
          onCancel={() => isActiveTrialShow(false)}
        />
      )}
      {isShowTrialEnded && (
        <TrialEndedModal
          show={isShowTrialEnded}
          onCancel={() => isTrialEndedShow(false)}
        />
      )}
      {isShowUpgrade && (
        <UpgradePlanModal
          show={isShowUpgrade}
          onCancel={() => isUpgradeShow(false)}
        />
      )}
      <ProjectLimitModal />
      <PublishLimitModal />
      {publishModal.isActive && (
        <PublishModal
          show={publishModal.isActive}
          onCancel={() =>
            isPublishShow({ isActive: false, pageId: null, sharePage: false })
          }
        />
      )}
      {templateModal.isActive && (
        <TemplateModal
          showModal={templateModal.isActive}
          handleCloseModal={() =>
            isShowTemplateModal({ isActive: false, page: null })
          }
        />
      )}
      {moveItemState.isActive && (
        <MoveModal
          show={moveItemState.isActive}
          onCancel={() => moveItem({ ...moveItemState, isActive: false })}
        />
      )}
      {renameFolderState.isActive && (
        <RenameFolderModal
          show={renameFolderState.isActive}
          onCancel={() => renameFolder({ isActive: false, folderId: null })}
        />
      )}
      {isPaymentErr && (
        <CustomToast
          isShow={isPaymentErr}
          setShow={() => isPaymentError(false)}
          classes="px-2 px-md-0"
          background="bg-danger"
          text="text-center text-white"
          message={`${t("payment.Something went wrong. Try again later or contact support.")}`}
        />
      )}
      {isBlocked && (
        <CustomToast
          isShow={isBlocked}
          setShow={() => isHaveBeenBlocked(false)}
          classes="px-2 px-md-0"
          background="bg-danger"
          text="text-center text-white"
          message={`${t("validation.Your account has been blocked")}`}
        />
      )}
      {isShowNewProjectModal && (
        <CreateProjectModal
          showModal={isShowProjectModal}
          closeModal={() => isShowNewProjectModal(false)}
        />
      )}
    </>
  );
};

export default Layout;
